<template>
  <div class="user-offer-system">
    <!-- Auto-save loading overlay -->
    <v-overlay :value="isSavingExercise" absolute>
      <div class="text-center">
        <v-progress-circular indeterminate color="amber" size="64" />
        <p class="mt-4 white--text">Saving exercise...</p>
      </div>
    </v-overlay>

    <v-row>
      <v-col cols="12">
        <exercise-logic-id ref="exerciseLogic" exCollection="offerSystemExCollection" refCollection="" :doc="doc"
          :shared="false" :projectShared="false" :pid="true" :eid="$route.params.eid" :synced="false" @ready="onReady"
          @error="onError" @saved="onSaved" @savedNext="onSavedNext" @loadedEx="onLoadedEx" @modifyEx="onModifyEx"
          @loadedRef="onLoadedRef">
          <template slot-scope="{ saveExercise }">
            <!-- Header Controls -->
            <div class="d-flex justify-space-between align-center mb-4">
              <h2 class="headline mb-0">Offer Eco-System (Facilitated)</h2>
              <div class="d-flex align-center ga-1">
                <v-btn icon title="Settings" @click="settingsDialog = true">
                  <v-icon>mdi-cog-outline</v-icon>
                </v-btn>
              </div>
            </div>

            <v-row>
              <v-col cols="12">
                <v-text-field v-model.trim="doc.title" label="Title" color="amber" />
              </v-col>
            </v-row>

            <div class="d-flex align-center mb-4">
              <span class="text-subtitle-2 mr-3">Columns</span>
              <v-chip-group v-model="doc.cols" mandatory active-class="amber black--text">
                <v-chip v-for="opt in columnPillOptions" :key="opt" :value="opt" small>
                  {{ opt }}
                </v-chip>
              </v-chip-group>
            </div>

            <!-- Arrange Interface (from former Step 3) -->
            <div class="d-flex justify-space-between align-center mb-4 ">
              <div class="d-flex align-center">
                <v-btn light :outlined="!minimalView" :color="minimalView ? 'amber' : 'grey'" small
                  @click="minimalView = !minimalView">
                  <v-icon small left>{{ minimalView ? 'mdi-text-short' : 'mdi-text' }}</v-icon>
                  {{ minimalView ? 'Minimal' : 'Full' }}
                </v-btn>
              </div>
              <div class="d-flex align-center ga-1">
                <v-btn light outlined color="grey" class="step2-add-btn" @click.stop="openAddGroup()">
                  <v-icon small left>mdi-plus</v-icon>
                  Add Group
                </v-btn>

                <v-btn icon title="Settings" @click="settingsDialog = true">
                  <v-icon>mdi-cog-outline</v-icon>
                </v-btn>
              </div>
            </div>

            <v-sheet :color="themeColor" class="mt-4 pa-4">
              <draggable :list="doc.list" :class="doc.cols === 'auto' ? 'auto' : 'row'" group="outer" id="iso">
                <structure-col v-for="(group, index) in doc.list" :key="group.id || `group-${index}`"
                  v-model="group.children" :item="group" :cardsById="cardsById" :attrs="structureColAttrs"
                  class="iso-item" @editGroup="openEditGroup" @onDeleteList="onDeleteList"
                  @addCard="openAddCardIntoGroup" @editCard="openEditCard" @deleteCard="softDeleteCard"
                  @cloneCard="cloneCard" @cardMoved="onCardMoved" />
              </draggable>
            </v-sheet>

            <!-- Deleted Cards Section -->
            <template v-if="deletedCards.length">
              <h2 class="subtitle-2 mt-5">Deleted items</h2>
              <structure-col :item="{ id: 'deleted', title: 'Deleted Items', name: 'deleted' }"
                :value="deletedCardNodes" :cardsById="cardsById"
                :attrs="{ ...structureColAttrs, add: false, controls: false }" @editCard="openEditCard" />
            </template>

            <bottom-navigation :save="true" :next="false" :prev="false" :gridView="false" :gridToggle="false"
              list="offer-ecosystem-facilitated" @save="saveExercise(true)" />
          </template>
        </exercise-logic-id>
      </v-col>
    </v-row>

    <!-- Add/Edit Card Dialog -->
    <v-dialog v-model="cardDialog" max-width="1200">
      <v-card>
        <v-card-text>
          <item-form ref="cardDialogForm" :doc="doc" :item="editingCard" :default-group="targetGroupId"
            @submit="upsertCard" @cancel="closeCardDialog" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Add/Edit Group Dialog -->
    <v-dialog v-model="groupDialog" max-width="1200">
      <v-card>

        <v-card-text>
          <col-form ref="groupDialogForm" :doc="doc" :item="editingGroup" @submit="upsertGroup"
            @cancel="closeGroupDialog" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Delete Group Confirmation Dialog -->
    <v-dialog v-model="deleteGroupDialog" max-width="450">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="warning">mdi-alert</v-icon>
          Delete Group?
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-2">
            Are you sure you want to delete <strong>"{{ deletingGroup ? deletingGroup.title : '' }}"</strong>?
          </p>
          <v-alert v-if="deletingGroupCardCount > 0" type="warning" dense text class="mb-0">
            This group contains <strong>{{ deletingGroupCardCount }}</strong>
            item{{ deletingGroupCardCount === 1 ? '' : 's' }} (including nested items).
            All items will be moved to the <strong>Deleted Items</strong> section and can be recovered later.
          </v-alert>
          <p v-else class="text-body-2 grey--text mb-0">
            This group is empty. The group structure will be preserved in case you need to restore it.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelDeleteGroup">Cancel</v-btn>
          <v-btn color="red" text @click="executeDeleteGroup">
            <v-icon left small>mdi-delete</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Saved Confirmation Dialog -->
    <v-dialog v-model="savedDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">
          {{ $t('exercise_service.saved.title') }}
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn color="amber" text @click="savedDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Settings Dialog -->
    <v-dialog v-model="settingsDialog" max-width="900">
      <v-card>
        <v-card-title class="headline d-flex justify-space-between align-center">
          <span>Settings</span>
          <v-btn color="amber" @click="settingsDialog = false" light>Apply</v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="4">

              <v-select v-model="doc.cols" label="Column width" :items="columnOptions" />
              <v-select v-model="doc.theme" label="Theme" :items="themeOptions" />
              <v-select v-model="doc.type" label="Type" :items="typeOptions" />

            </v-col>

            <v-col cols="8">
              <div class="d-flex flex-wrap">
                <v-switch v-for="toggle in toggleOptions" :key="toggle.key" v-model="doc[toggle.key]"
                  :label="toggle.label" color="amber" class="mr-3 ml-3" inset />
              </div>
            </v-col>

            <v-col cols="6">
              <v-text-field label="Categories label" v-model.trim="doc.local.labels.categories" type="text"
                placeholder="categories" color="amber" />
              <unit-sub-items-with-id v-model="doc.local.categories" id-prefix="cat" :dark="false" class="mb-4">
                <v-label light>Define the {{ doc.local.labels.categories || 'categories' }} for each</v-label>
                <p class="ma-0">Type below and press <strong>Add</strong></p>
              </unit-sub-items-with-id>

              <v-text-field label="Tags label" v-model.trim="doc.local.labels.tags" type="text" placeholder="tags"
                color="amber" />
              <unit-sub-items-with-id v-model="doc.local.tags" id-prefix="tag" :dark="false">
                <v-label light>Define the {{ doc.local.labels.tags || 'tags' }} for each</v-label>
                <p class="ma-0">Type below and press <strong>Add</strong></p>
              </unit-sub-items-with-id>
            </v-col>

            <v-col cols="6">
              <v-text-field label="Assets label" v-model.trim="doc.local.labels.assets" type="text" placeholder="assets"
                color="amber" />
              <unit-sub-items-with-id v-model="doc.local.assets" id-prefix="asset" :dark="false" class="mb-4">
                <v-label light>Define the {{ doc.local.labels.assets || 'assets' }} for each</v-label>
                <p class="ma-0">Type below and press <strong>Add</strong></p>
              </unit-sub-items-with-id>

              <v-text-field label="Audiences label" v-model.trim="doc.local.labels.audiences" type="text"
                placeholder="audiences" color="amber" />
              <unit-sub-items-with-id v-model="doc.local.audiences" id-prefix="aud" :dark="false">
                <v-label light>Define the {{ doc.local.labels.audiences || 'audiences' }} for each</v-label>
                <p class="ma-0">Type below and press <strong>Add</strong></p>
              </unit-sub-items-with-id>
            </v-col>

            <v-col cols="12">
              <p class="text-subtitle-2 mb-2">Features &amp; Benefits labels</p>
              <v-row>
                <v-col cols="6">
                  <v-text-field label="Features label" v-model.trim="doc.local.labels.features" type="text"
                    placeholder="features" color="amber" />
                </v-col>
                <v-col cols="6">
                  <v-text-field label="Benefits label" v-model.trim="doc.local.labels.benefits" type="text"
                    placeholder="benefits" color="amber" />
                </v-col>
              </v-row>
            </v-col>
          </v-row>


        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="amber" @click="settingsDialog = false" light>Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Debug Panel (only in development) -->
    <v-expansion-panels v-if="isDev" class="mt-4">
      <v-expansion-panel>
        <v-expansion-panel-header>Debug Info</v-expansion-panel-header>
        <v-expansion-panel-content>
          <pre class="debug-pre">{{ debugInfo }}</pre>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import draggable from 'vuedraggable'

import { BottomNavigation } from '@/components/core'
import { ExerciseLogicId } from '@/components/exercises'
import { StructureCol, ItemForm, ColForm, UnitSubItemsWithId } from '@/views/user/exercises/offer-system-facilitated'
import { docModel, deepClone, generateId } from '@/views/user/exercises/offer-system-facilitated/model'

const fb = require('@/firebaseConfig.js')

const COLUMN_OPTIONS = ['1', '2', '3', '4', '6', '12', 'auto']
const COLUMN_PILL_OPTIONS = ['1', '2', '3', '4', '6', '12']
const THEME_OPTIONS = ['dark', 'light']
const TYPE_OPTIONS = ['large', 'medium', 'small', 'waterfall']

const TOGGLE_OPTIONS = [
  { key: 'controls', label: 'Controls' },
  { key: 'note', label: 'Description' },
  { key: 'tags', label: 'Tags' },
  { key: 'contribution', label: 'Brand contribution' },
  { key: 'stats', label: 'Asset classes' },
  { key: 'graph', label: 'Asset mix graph' },
  { key: 'children', label: 'Children' },
  { key: 'edit', label: 'Edit' },
]

export default {
  name: 'UserOfferSystemFacilitated',

  components: {
    BottomNavigation,
    ExerciseLogicId,
    StructureCol,
    UnitSubItemsWithId,
    ItemForm,
    ColForm,
    draggable,
  },

  data() {
    return {
      // Options
      columnOptions: COLUMN_OPTIONS,
      columnPillOptions: COLUMN_PILL_OPTIONS,
      themeOptions: THEME_OPTIONS,
      typeOptions: TYPE_OPTIONS,
      toggleOptions: TOGGLE_OPTIONS,

      // UI State
      savedDialog: false,
      cardDialog: false,
      groupDialog: false,
      settingsDialog: false,
      deleteGroupDialog: false,
      isSavingExercise: false,
      isAutoSaving: false,

      // Arrange State
      minimalView: false,

      // Editing State
      editingCardId: null,
      editingGroupId: null,
      targetGroupId: null,
      deletingGroupId: null,

      // Data
      cardsById: {},
      doc: deepClone(docModel),
      exerciseId: null,

      // Internal
      cardsUnsub: null,
      pruneDebounceTimer: null,
    }
  },

  computed: {
    ...mapState(['userProfile', 'currentUser', 'currentProject', 'currentProjectModel']),

    isDev() {
      return process.env.NODE_ENV === 'development'
    },

    pid() {
      return this.$route.params.pid || this.currentProjectModel?.id || null
    },

    eid() {
      const routeEid = this.$route.params.eid
      if (routeEid && routeEid !== 'create') {
        return routeEid
      }
      return this.exerciseId || null
    },

    isEditingCard() {
      return !!this.editingCardId
    },

    editingCard() {
      return this.editingCardId ? this.cardsById[this.editingCardId] : null
    },

    isEditingGroup() {
      return !!this.editingGroupId
    },

    editingGroup() {
      return this.editingGroupId ? this.doc.list.find((g) => g.id === this.editingGroupId) : null
    },

    deletingGroup() {
      return this.deletingGroupId ? this.doc.list.find((g) => g.id === this.deletingGroupId) : null
    },

    deletingGroupCardCount() {
      if (!this.deletingGroup) return 0
      return this.countNestedCards(this.deletingGroup.children)
    },

    themeColor() {
      return this.doc.theme === 'light' ? 'theme--dark grey lighten-4' : 'grey darken-3'
    },

    allCards() {
      return Object.values(this.cardsById)
    },

    activeCards() {
      return this.allCards.filter((c) => !c.deleted)
    },

    deletedCards() {
      return this.allCards.filter((c) => c.deleted)
    },

    deletedCardNodes() {
      return this.deletedCards.map((c) => ({ id: c.id, children: [] }))
    },

    structureColAttrs() {
      return {
        cols: this.doc.cols,
        controls: this.doc.controls,
        theme: this.doc.theme,
        graph: this.doc.graph,
        children: this.doc.children,
        type: this.doc.type,
        note: this.doc.note,
        stats: this.doc.stats,
        tags: this.doc.tags,
        contribution: this.doc.contribution,
        add: true,
        local: this.doc.local,
        minimal: this.minimalView,
      }
    },

    debugInfo() {
      return {
        editingCardId: this.editingCardId,
        cardCount: this.allCards.length,
        activeCount: this.activeCards.length,
        deletedCount: this.deletedCards.length,
        groupCount: this.doc.list?.length || 0,
      }
    },
  },

  watch: {
    'doc.list': {
      handler(newVal) {
        const ensureNestedChildren = (children) => {
          if (!Array.isArray(children)) return
          for (const child of children) {
            if (child && typeof child === 'object') {
              if (!Array.isArray(child.children)) {
                this.$set(child, 'children', [])
              } else {
                ensureNestedChildren(child.children)
              }
            }
          }
        }

        for (const group of newVal || []) {
          if (group && !Array.isArray(group.children)) {
            this.$set(group, 'children', [])
          } else if (group) {
            ensureNestedChildren(group.children)
          }
        }
      },
      deep: true,
    },
  },

  created() {
    this.doc = deepClone(docModel)
  },

  beforeDestroy() {
    this.cleanup()
  },

  methods: {
    cleanup() {
      if (this.cardsUnsub) {
        this.cardsUnsub()
        this.cardsUnsub = null
      }
      if (this.pruneDebounceTimer) {
        clearTimeout(this.pruneDebounceTimer)
        this.pruneDebounceTimer = null
      }
    },

    onReady() { },

    onError(error) {
      console.error('[UserOfferSystemFacilitated] Error:', error)
    },

    onSaved(payload) {
      if (payload?.id) {
        this.exerciseId = payload.id
        this.redirectIfCreate(payload.id)
      }
      if (!this.isAutoSaving) {
        this.savedDialog = true
      }
      if (this.eid && !this.cardsUnsub) {
        this.subscribeToCards()
      }
    },

    onSavedNext(payload) {
      if (payload?.id) {
        this.exerciseId = payload.id
        this.redirectIfCreate(payload.id)
      }
      if (this.eid && !this.cardsUnsub) {
        this.subscribeToCards()
      }
    },

    redirectIfCreate(newEid) {
      if (this.$route.params.eid === 'create' && newEid) {
        const newPath = this.$route.path.replace('/create', `/${newEid}`)
        this.$router.replace(newPath)
      }
    },

    onLoadedRef() { },

    onModifyEx() { },

    onLoadedEx(items) {
      if (!items?.length) return

      if (items[0].id) {
        this.exerciseId = items[0].id
      }

      this.mergeExerciseData(items[0].doc)
      this.normalizeLayout()
      this.subscribeToCards()
    },

    subscribeToCards() {
      this.cleanup()

      const pid = this.pid
      const eid = this.eid

      if (!pid || typeof pid !== 'string' || !pid.trim()) {
        console.warn('[UserOfferSystemFacilitated] Invalid pid, cannot subscribe to cards', { pid })
        return
      }

      if (!eid || typeof eid !== 'string' || !eid.trim() || eid === 'create') {
        console.warn('[UserOfferSystemFacilitated] Invalid eid, cannot subscribe to cards - save the exercise first', { eid })
        return
      }

      this.cardsUnsub = fb.db
        .collection('offerSystemExCollectionCards')
        .where('pid', '==', pid)
        .where('eid', '==', eid)
        .onSnapshot(
          (snapshot) => {
            const cards = {}
            snapshot.forEach((doc) => {
              cards[doc.id] = { id: doc.id, ...doc.data() }
            })
            this.cardsById = cards
            this.debouncedPruneLayout()
          },
          (error) => {
            console.error('[UserOfferSystemFacilitated] Cards subscription error:', error)
          }
        )
    },

    debouncedPruneLayout() {
      if (this.pruneDebounceTimer) {
        clearTimeout(this.pruneDebounceTimer)
      }
      this.pruneDebounceTimer = setTimeout(() => {
        this.pruneOrphanedCardIds()
      }, 150)
    },

    pruneOrphanedCardIds() {
      const validIds = new Set(Object.keys(this.cardsById))

      const pruneNodes = (nodes) => {
        if (!Array.isArray(nodes)) return []

        const result = []
        const stack = [...nodes]
        const processed = new WeakSet()

        while (stack.length > 0) {
          const node = stack.shift()

          if (!node || processed.has(node)) continue
          processed.add(node)

          if (!node.id || !validIds.has(node.id)) {
            continue
          }

          const prunedChildren = node.children ? pruneNodes(node.children) : []
          result.push({
            id: node.id,
            children: prunedChildren,
          })
        }

        return result
      }

      for (const group of this.doc.list || []) {
        const pruned = pruneNodes(group.children || [])
        if (JSON.stringify(pruned) !== JSON.stringify(group.children)) {
          group.children = pruned
        }
      }
    },

    normalizeChildren(children) {
      if (!Array.isArray(children)) return []

      return children
        .map((child) => {
          if (typeof child === 'string') {
            return { id: child, children: [] }
          }
          if (child && typeof child === 'object' && child.id) {
            return {
              id: String(child.id),
              children: this.normalizeChildren(child.children),
            }
          }
          return null
        })
        .filter(Boolean)
    },

    normalizeLayout() {
      for (const group of this.doc.list || []) {
        if (!Array.isArray(group.children)) {
          this.$set(group, 'children', [])
          continue
        }

        group.children = this.normalizeChildren(group.children)
      }
    },

    async openAddCardIntoGroup(groupId) {
      if (!this.eid) {
        await this.ensureExerciseSaved()
        if (!this.eid) {
          console.warn('[UserOfferSystemFacilitated] Could not save exercise, cannot add card')
          return
        }
      }

      this.editingCardId = null
      this.targetGroupId = groupId || null
      this.cardDialog = true
    },

    ensureExerciseSaved() {
      return new Promise((resolve) => {
        if (this.eid) {
          resolve()
          return
        }

        this.isSavingExercise = true
        this.isAutoSaving = true

        const unwatch = this.$watch('exerciseId', (newId) => {
          if (newId) {
            unwatch()
            this.isSavingExercise = false
            this.isAutoSaving = false
            this.$nextTick(() => {
              resolve()
            })
          }
        })

        if (this.$refs.exerciseLogic) {
          this.$refs.exerciseLogic.saveExercise(true)
        } else {
          console.error('[UserOfferSystemFacilitated] Exercise logic ref not found')
          unwatch()
          this.isSavingExercise = false
          this.isAutoSaving = false
          resolve()
        }

        setTimeout(() => {
          unwatch()
          this.isSavingExercise = false
          this.isAutoSaving = false
          resolve()
        }, 5000)
      })
    },

    openEditCard(cardId) {
      this.editingCardId = cardId
      this.targetGroupId = null
      this.cardDialog = true
    },

    closeCardDialog() {
      this.cardDialog = false
      this.editingCardId = null
      this.targetGroupId = null
      if (this.$refs.cardDialogForm?.reset) {
        this.$refs.cardDialogForm.reset()
      }
    },

    openAddGroup() {
      this.editingGroupId = null
      this.groupDialog = true
    },

    openEditGroup(groupId) {
      this.editingGroupId = groupId
      this.groupDialog = true
    },

    closeGroupDialog() {
      this.groupDialog = false
      this.editingGroupId = null
      if (this.$refs.groupDialogForm?.reset) {
        this.$refs.groupDialogForm.reset()
      }
    },

    upsertGroup(draft) {
      if (!draft.title?.trim()) return

      const cleanDraft = { ...draft }
      delete cleanDraft.id
      delete cleanDraft.children

      if (this.isEditingGroup) {
        const index = this.doc.list.findIndex((g) => g.id === this.editingGroupId)
        if (index !== -1) {
          const existing = this.doc.list[index]
          this.$set(this.doc.list, index, {
            ...existing,
            ...cleanDraft,
            id: existing.id,
            children: existing.children || [],
          })
        }
      } else {
        const newGroup = {
          ...cleanDraft,
          id: generateId('group'),
          children: [],
        }
        this.$set(this.doc, 'list', [...this.doc.list, newGroup])
      }

      this.closeGroupDialog()
    },

    confirmDeleteGroup(groupId) {
      this.deletingGroupId = groupId
      this.deleteGroupDialog = true
    },

    cancelDeleteGroup() {
      this.deleteGroupDialog = false
      this.deletingGroupId = null
    },

    countNestedCards(children) {
      if (!Array.isArray(children)) return 0
      let count = 0
      for (const node of children) {
        if (node?.id) {
          count++
          if (Array.isArray(node.children)) {
            count += this.countNestedCards(node.children)
          }
        }
      }
      return count
    },

    collectNestedCardIds(children, ids = []) {
      if (!Array.isArray(children)) return ids
      for (const node of children) {
        if (node?.id) {
          ids.push(node.id)
          if (Array.isArray(node.children)) {
            this.collectNestedCardIds(node.children, ids)
          }
        }
      }
      return ids
    },

    async executeDeleteGroup() {
      if (!this.deletingGroupId) return

      const group = this.doc.list.find((g) => g.id === this.deletingGroupId)
      if (!group) {
        this.cancelDeleteGroup()
        return
      }

      try {
        const cardIds = this.collectNestedCardIds(group.children || [])

        const deletedGroupRecord = {
          ...deepClone(group),
          deletedAt: new Date().toISOString(),
          cardCount: cardIds.length,
        }

        if (!Array.isArray(this.doc.deletedGroups)) {
          this.$set(this.doc, 'deletedGroups', [])
        }
        this.doc.deletedGroups.push(deletedGroupRecord)

        const batchPromises = cardIds.map((cardId) =>
          fb.db
            .collection('offerSystemExCollectionCards')
            .doc(cardId)
            .set(
              {
                deleted: true,
                deletedFromGroup: group.id,
                deletedAt: new Date(),
                updatedAt: new Date(),
              },
              { merge: true }
            )
        )

        await Promise.all(batchPromises)

        const index = this.doc.list.findIndex((g) => g.id === this.deletingGroupId)
        if (index !== -1) {
          this.doc.list.splice(index, 1)
        }
      } catch (error) {
        console.error('[UserOfferSystemFacilitated] Failed to delete group:', error)
      }

      this.cancelDeleteGroup()
    },

    async upsertCard(draft) {
      const pid = this.pid
      const eid = this.eid

      if (!pid || typeof pid !== 'string' || !pid.trim()) {
        console.error('[UserOfferSystemFacilitated] Cannot save card: invalid pid', { pid })
        alert('Error: Project ID is missing. Please refresh the page.')
        return
      }

      if (!eid || typeof eid !== 'string' || !eid.trim()) {
        console.error('[UserOfferSystemFacilitated] Cannot save card: invalid eid', { eid })
        alert('Please save the exercise first before adding items.')
        return
      }

      try {
        const payload = {
          ...deepClone(draft),
          pid: this.pid,
          eid: this.eid,
          deleted: false,
          updatedAt: new Date(),
        }

        delete payload.id

        let cardId
        const assignedGroup = draft.group || (draft.groups && draft.groups[0]) || null

        if (this.isEditingCard) {
          cardId = this.editingCardId

          const oldCard = this.cardsById[cardId]
          const oldGroup = oldCard?.group || null

          await fb.db
            .collection('offerSystemExCollectionCards')
            .doc(cardId)
            .set(payload, { merge: true })

          if (assignedGroup && assignedGroup !== oldGroup) {
            this.syncCardToGroup(cardId, assignedGroup)
          }
        } else {
          payload.createdAt = new Date()
          const ref = await fb.db.collection('offerSystemExCollectionCards').add(payload)
          cardId = ref.id

          const targetGroup = assignedGroup || this.targetGroupId || null
          this.addCardToLayout(cardId, targetGroup)
        }

        this.closeCardDialog()
      } catch (error) {
        console.error('[UserOfferSystemFacilitated] Failed to save card:', error)
      }
    },

    async softDeleteCard(cardId) {
      if (!cardId) return

      try {
        await fb.db
          .collection('offerSystemExCollectionCards')
          .doc(cardId)
          .set({ deleted: true, updatedAt: new Date() }, { merge: true })

        this.removeCardFromLayout(cardId)
      } catch (error) {
        console.error('[UserOfferSystemFacilitated] Failed to delete card:', error)
      }
    },

    async cloneCard(cardId) {
      if (!cardId) return

      const originalCard = this.cardsById[cardId]
      if (!originalCard) {
        console.error('[UserOfferSystemFacilitated] Cannot clone: card not found', cardId)
        return
      }

      try {
        const clonedData = deepClone(originalCard)

        delete clonedData.id
        clonedData.name = `${clonedData.name || 'Untitled'} (Copy)`
        clonedData.createdAt = new Date()
        clonedData.updatedAt = new Date()
        clonedData.pid = this.pid
        clonedData.eid = this.eid
        clonedData.deleted = false

        const ref = await fb.db.collection('offerSystemExCollectionCards').add(clonedData)
        const newCardId = ref.id

        const inserted = this.insertCardAfter(cardId, newCardId)

        if (!inserted) {
          const groupId = originalCard.group || null
          this.addCardToLayout(newCardId, groupId)
        }
      } catch (error) {
        console.error('[UserOfferSystemFacilitated] Failed to clone card:', error)
      }
    },

    insertCardAfter(afterCardId, newCardId) {
      const newNode = { id: newCardId, children: [] }

      const insertInNodes = (nodes) => {
        if (!Array.isArray(nodes)) return false

        for (let i = 0; i < nodes.length; i++) {
          if (nodes[i]?.id === afterCardId) {
            nodes.splice(i + 1, 0, newNode)
            return true
          }

          if (nodes[i]?.children && insertInNodes(nodes[i].children)) {
            return true
          }
        }

        return false
      }

      for (const group of this.doc.list || []) {
        if (insertInNodes(group.children || [])) {
          return true
        }
      }

      return false
    },

    addCardToLayout(cardId, groupId = null) {
      if (!cardId) return

      const newNode = { id: cardId, children: [] }

      if (groupId) {
        const group = this.doc.list?.find((g) => g.id === groupId)
        if (group) {
          if (!Array.isArray(group.children)) {
            this.$set(group, 'children', [])
          }
          group.children.push(newNode)
          return
        }
      }

      if (this.doc.list?.length) {
        const firstGroup = this.doc.list[0]
        if (!Array.isArray(firstGroup.children)) {
          this.$set(firstGroup, 'children', [])
        }
        firstGroup.children.push(newNode)
        return
      }

      this.doc.list = [
        {
          id: generateId('group'),
          title: 'Unassigned',
          name: 'unassigned',
          children: [newNode],
        },
      ]
    },

    removeCardFromLayout(cardId) {
      const removeFromNodes = (nodes, visited = new WeakSet()) => {
        if (!Array.isArray(nodes)) return []

        return nodes
          .filter((n) => n && n.id !== cardId)
          .map((n) => {
            if (visited.has(n)) return null
            visited.add(n)
            return {
              id: n.id,
              children: removeFromNodes(n.children || [], visited),
            }
          })
          .filter(Boolean)
      }

      for (const group of this.doc.list || []) {
        group.children = removeFromNodes(group.children || [])
      }
    },

    syncCardToGroup(cardId, groupId) {
      if (!cardId) return

      this.removeCardFromLayout(cardId)

      if (groupId) {
        this.addCardToLayout(cardId, groupId)
      }
    },

    findGroupForCard(cardId) {
      if (!cardId) return null

      const findInChildren = (nodes) => {
        if (!Array.isArray(nodes)) return false
        return nodes.some((n) => n?.id === cardId || findInChildren(n?.children))
      }

      for (const group of this.doc.list || []) {
        if (findInChildren(group.children)) {
          return group.id
        }
      }

      return null
    },

    async onCardMoved({ cardId }) {
      if (!cardId) return

      const currentGroup = this.findGroupForCard(cardId)

      try {
        await fb.db
          .collection('offerSystemExCollectionCards')
          .doc(cardId)
          .set({ group: currentGroup, updatedAt: new Date() }, { merge: true })
      } catch (error) {
        console.error('[UserOfferSystemFacilitated] Failed to sync card group:', error)
      }
    },

    onDeleteList({ id }) {
      this.confirmDeleteGroup(id)
    },

    mergeExerciseData(data) {
      if (!data) return

      for (const [key, value] of Object.entries(data)) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          if (!this.doc[key] || typeof this.doc[key] !== 'object') {
            this.$set(this.doc, key, {})
          }
          for (const [innerKey, innerValue] of Object.entries(value)) {
            this.$set(this.doc[key], innerKey, innerValue)
          }
        } else {
          this.$set(this.doc, key, value)
        }
      }

      if (!Array.isArray(this.doc.list)) {
        this.$set(this.doc, 'list', [])
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.user-offer-system {
  .auto {
    display: flex;
    flex-wrap: nowrap;

    .col-auto {
      width: auto;
      flex: 1;
    }
  }

  .group-card,
  .item-card {
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #ffc107;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &__actions {
      display: flex;
      gap: 4px;
      opacity: 0.6;
      transition: opacity 0.2s ease;
    }

    &:hover &__actions {
      opacity: 1;
    }
  }

  .item-card--unassigned {
    border-color: #ff9800;
    border-style: dashed;
  }

  .step2-panel-header {
    .step2-add-btn {
      opacity: 1;
      transition: opacity 0.2s ease;
    }

    &:hover .step2-add-btn {
      opacity: 1;
    }
  }

  .debug-pre {
    font-size: 12px;
    max-height: 300px;
    overflow: auto;
    background: #f5f5f5;
    padding: 12px;
    border-radius: 4px;
  }
}
</style>
